import React, { useEffect, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import { useAppSelector } from "../../../store/hooks.ts";
import { calculateFeeEstimate } from "../../calendar/helper/feeCalculation.ts";
import { Box, Typography } from "@mui/material";
import globalAxios from "../../../globalAxios.js";
import { SERVER_URL } from "../../../config.js";

type ChartData = {
	months: string[];
	totalsEstimate: number[];
	totalsActual: number[];
	totalsGap: number[];
};

const currency = (n: number) =>
	new Intl.NumberFormat("en-AU", {
		style: "currency",
		currency: "AUD",
	}).format(n || 0);

const MonthlyIncomeChart = () => {
	const chartRef = useRef<ReactECharts>(null);

	const sessions = useAppSelector((state) => state.calendar.sessions);
	const userSettings = useAppSelector((state) => state.user.settings);
	const reduxSchedules = useAppSelector((state) => state.schedules.data);

	const reportStart = useAppSelector((state) => state.reports.startDate);
	const reportEnd = useAppSelector((state) => state.reports.endDate);

	const [localSchedules, setLocalSchedules] = useState<any[]>([]);
	const [totalsSummary, setTotalsSummary] = useState({
		estimate: 0,
		actual: 0,
	});

	const schedules =
		localSchedules.length > 0 ? localSchedules : reduxSchedules;

	const [chartData, setChartData] = useState<ChartData>({
		months: [],
		totalsEstimate: [],
		totalsActual: [],
		totalsGap: [],
	});

	// ————— utils —————
	const extractItemNumbersFromRedux = (sessionsList: any[]) => {
		const itemNumbers: (string | number)[] = [];
		sessionsList.forEach((session) => {
			session.events?.forEach((subEvent: any) => {
				try {
					if (
						!subEvent.actual_items ||
						subEvent.actual_items === "null"
					)
						return;
					const parsed = JSON.parse(subEvent.actual_items);
					if (!Array.isArray(parsed)) return;
					const numericItems = parsed.filter((it) => !isNaN(it));
					itemNumbers.push(...numericItems);
				} catch {
					/* ignore bad JSON */
				}
			});
		});
		return [...new Set(itemNumbers)];
	};

	const fetchSchedules = async (itemNumbers: (string | number)[]) => {
		if (itemNumbers.length === 0) return;
		try {
			const response = await globalAxios.get(`${SERVER_URL}/schedules`, {
				params: { itemNums: itemNumbers },
			});
			setLocalSchedules(response.data);
		} catch (error) {
			console.error("Error fetching schedules:", error);
		}
	};

	// Fetch schedules if needed
	useEffect(() => {
		if (!schedules || schedules.length === 0) {
			const itemNumbers = extractItemNumbersFromRedux(sessions || []);
			fetchSchedules(itemNumbers);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sessions, schedules]);

	// Recalculate stacked data
	useEffect(() => {
		if (
			!sessions ||
			!userSettings ||
			!reportStart ||
			!reportEnd ||
			!Array.isArray(sessions)
		)
			return;

		const start = new Date(reportStart);
		const end = new Date(reportEnd);
		if (isNaN(start.getTime()) || isNaN(end.getTime())) return;

		start.setDate(1);
		end.setDate(1);

		const fullMonths: string[] = [];
		const iter = new Date(start);
		while (iter <= end) {
			const key = `${iter.getFullYear()}-${String(
				iter.getMonth() + 1
			).padStart(2, "0")}`;
			fullMonths.push(key);
			iter.setMonth(iter.getMonth() + 1);
		}

		const estimateMap: Record<string, number> = {};
		const actualMap: Record<string, number> = {};

		sessions.forEach((session) => {
			const events = session?.events || [];
			const fullDateStr = `${session.date_of_service}T${session.start_time}:00`;
			const fullDate = new Date(fullDateStr);
			if (isNaN(fullDate.getTime())) return;
			const isoMonth = `${fullDate.getFullYear()}-${String(
				fullDate.getMonth() + 1
			).padStart(2, "0")}`;

			if (!fullMonths.includes(isoMonth)) return;

			const estimate =
				schedules && schedules.length
					? calculateFeeEstimate(userSettings, events, schedules) || 0
					: 0;

			const actual = events.reduce((sum: number, ev: any) => {
				const raw = ev?.actual_fee ?? "";
				const num = Number(raw);
				return Number.isFinite(num) ? sum + num : sum;
			}, 0);

			estimateMap[isoMonth] = (estimateMap[isoMonth] || 0) + estimate;
			actualMap[isoMonth] = (actualMap[isoMonth] || 0) + actual;
		});

		const totalsEstimate = fullMonths.map((m) =>
			parseFloat((estimateMap[m] || 0).toFixed(2))
		);
		const totalsActual = fullMonths.map((m) =>
			parseFloat((actualMap[m] || 0).toFixed(2))
		);
		const totalsGap = fullMonths.map((m) => {
			const gap = (estimateMap[m] || 0) - (actualMap[m] || 0);
			return parseFloat(Math.max(gap, 0).toFixed(2));
		});

		const totalEstimate = totalsEstimate.reduce((s, v) => s + v, 0);
		const totalActual = totalsActual.reduce((s, v) => s + v, 0);

		const displayMonths = fullMonths.map((iso) => {
			const [year, month] = iso.split("-");
			return new Date(Number(year), Number(month) - 1).toLocaleString(
				"default",
				{ month: "short", year: "numeric" }
			);
		});

		setTotalsSummary({ estimate: totalEstimate, actual: totalActual });
		setChartData({
			months: displayMonths,
			totalsEstimate,
			totalsActual,
			totalsGap,
		});
	}, [sessions, userSettings, schedules, reportStart, reportEnd]);

	// Resize
	useEffect(() => {
		const onResize = () => {
			chartRef.current?.getEchartsInstance().resize();
		};
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);

	if (!chartData.months.length) {
		return (
			<Typography variant="body2">
				No income data available for chart.
			</Typography>
		);
	}

	const options = {
		title: {
			text: "Monthly Income",
			left: "center",
			textStyle: { fontSize: 16, fontWeight: "bold" },
			subtext: `Total Actual: ${currency(
				totalsSummary.actual
			)} | Total Estimate: ${currency(totalsSummary.estimate)}  `,
			subtextStyle: { fontSize: 12, lineHeight: 16 },
		},
		tooltip: {
			trigger: "axis",
			axisPointer: { type: "shadow" },
			formatter: (params: any[]) => {
				const idx = params?.[0]?.dataIndex ?? 0;
				const month = chartData.months[idx];
				const actual = chartData.totalsActual[idx] || 0;
				const gap = chartData.totalsGap[idx] || 0;
				const estimate =
					chartData.totalsEstimate[idx] || actual + gap || 0;
				return `
          <strong>${month}</strong><br/>
          Total Actual: ${currency(actual)}<br/>
          Total Estimate: ${currency(estimate)}${
					gap > 0 ? `<br/>Gap to Estimate: ${currency(gap)}` : ""
				}`;
			},
		},
		legend: {
			top: 65,
			data: ["Total Actual", "Total Estimate"],
		},
		xAxis: {
			type: "category",
			data: chartData.months,
			axisLabel: { rotate: 45 },
		},
		yAxis: {
			type: "value",
			name: "Amount (AUD)",
			axisLabel: { formatter: (val: number) => `$${val.toFixed(0)}` },
		},
		grid: {
			left: "5%",
			right: "5%",
			bottom: "20%",
			top: 100,
			containLabel: true,
		},
		series: [
			{
				name: "Total Actual",
				type: "bar",
				stack: "total",
				data: chartData.totalsActual,
				itemStyle: { color: "#32CD32" }, // lime green
				barWidth: "50%",
			},
			{
				name: "Total Estimate",
				type: "bar",
				stack: "total",
				data: chartData.totalsGap,
				itemStyle: { color: "#A020F0" }, // bright purple
				barWidth: "50%",
			},
		],
	};

	return (
		<Box mt={4}>
			<ReactECharts
				ref={chartRef}
				option={options}
				style={{ width: "100%", height: "500px" }}
				notMerge
			/>
		</Box>
	);
};

export default MonthlyIncomeChart;
